import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ClipboardPaste, Lightbulb, Mail, MessageSquareQuote, Mic, MicOff, PhoneIncoming, PhoneOutgoing, ShieldAlert, Sparkles, Target, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime, fmtDuration, fmtPhone, scoreTone } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loading, Pill, ScoreBadge, sentimentTone, Spinner } from "@/components/common";
import { cn } from "@/lib/utils";

const DIMS = [
  ["compliance", "Compliance & accuracy"],
  ["discovery", "Discovery"],
  ["solution_framing", "Solution framing"],
  ["closing_next_steps", "Closing & next steps"],
  ["empathy_tone", "Empathy & tone"],
];

function Bullets({ title, items, icon: Icon, tone, testId }) {
  if (!items?.length) return null;
  return (
    <div data-testid={testId}>
      <div className={cn("flex items-center gap-1.5 eyebrow mb-2", tone)}>
        <Icon className="h-3.5 w-3.5" /> {title}
      </div>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-slate-300 leading-relaxed pl-3 border-l border-[#2d3d58]">{typeof it === "string" ? it : `${it.time ? `[${it.time}] ` : ""}${it.note}`}</li>
        ))}
      </ul>
    </div>
  );
}

function TranscriptPaste({ callId, onDone }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const m = useMutation({
    mutationFn: () => api.post(`/calls/${callId}/transcript`, { text, send_email: sendEmail }).then((r) => r.data),
    onSuccess: () => { toast.success("Transcript saved and analyzed"); setOpen(false); setText(""); onDone(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <>
      <Button data-testid="call-paste-transcript-button" size="sm" variant="outline" className="h-8 text-xs border-[#2d3d58] bg-[#0f172a] hover:bg-[#161f30]" onClick={() => setOpen(true)}>
        <ClipboardPaste className="h-3.5 w-3.5 mr-1.5" /> Paste transcript
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111827] border-[#2d3d58] max-w-2xl" data-testid="paste-transcript-dialog">
          <DialogHeader><DialogTitle className="font-display">Add a transcript manually</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-400">Prefix lines with <span className="font-mono text-slate-200">Agent:</span> or <span className="font-mono text-slate-200">Customer:</span> for speaker labels. GPT-5.4 will score and coach the call immediately.</p>
          <Textarea data-testid="paste-transcript-textarea" rows={12} value={text} onChange={(e) => setText(e.target.value)} placeholder={"Agent: Thanks for calling Total Quality Insurance, this is Sonya...\nCustomer: Hi, I got a renewal notice and my premium went up..."} className="bg-[#0f172a] border-[#2d3d58] font-mono text-xs" />
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input data-testid="paste-transcript-send-email" type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="accent-blue-500" /> Email the coaching feedback to the agent (CC managers if below threshold)
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="paste-transcript-submit" size="sm" className="bg-blue-600 hover:bg-blue-500" disabled={m.isPending || text.trim().length < 20} onClick={() => m.mutate()}>
              {m.isPending ? <Spinner className="text-white" /> : <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Analyze</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function CallDrawer({ callId, onClose }) {
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["call", callId], queryFn: () => api.get(`/calls/${callId}`).then((r) => r.data), enabled: !!callId });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["call", callId] }); qc.invalidateQueries({ queryKey: ["calls"] }); qc.invalidateQueries({ queryKey: ["recent"] }); };
  const analyze = useMutation({ mutationFn: () => api.post(`/calls/${callId}/analyze`).then((r) => r.data), onSuccess: () => { toast.success("Call analyzed"); refresh(); }, onError: (e) => toast.error(errMsg(e)) });
  const resend = useMutation({ mutationFn: () => api.post(`/calls/${callId}/resend-feedback`).then((r) => r.data), onSuccess: (d) => { d.status === "sent" ? toast.success(`Feedback emailed to ${d.to.join(", ")}${d.cc.length ? ` (cc ${d.cc.join(", ")})` : ""}`) : toast.error(d.error || "Email failed"); refresh(); }, onError: (e) => toast.error(errMsg(e)) });

  const c = q.data;
  const a = c?.analysis;
  const tone = scoreTone(c?.score);

  return (
    <Sheet open={!!callId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-3xl bg-[#0f172a] border-[#1f293d] p-0 overflow-y-auto" data-testid="call-drawer">
        {q.isLoading || !c ? (
          <div className="p-6"><SheetTitle className="sr-only">Loading call</SheetTitle><Loading /></div>
        ) : (
          <>
            <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#1f293d] space-y-3">
              <div className="flex items-start gap-4">
                <div className={cn("shrink-0 h-20 w-20 rounded-lg border flex flex-col items-center justify-center", tone.bg, tone.border)}>
                  <span className={cn("metric text-3xl", tone.text)} data-testid="call-score">{c.score ?? "—"}</span>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">/ 100</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="eyebrow">{c.direction === "incoming" ? <span className="inline-flex items-center gap-1"><PhoneIncoming className="h-3 w-3" /> Inbound</span> : <span className="inline-flex items-center gap-1"><PhoneOutgoing className="h-3 w-3" /> Outbound</span>} · {fmtDuration(c.duration)} · {c.status}</div>
                  <SheetTitle className="font-display text-xl text-white mt-1" data-testid="call-title">{a?.topic || `Call with ${fmtPhone(c.client_number)}`}</SheetTitle>
                  <div className="text-sm text-slate-400 mt-1">
                    <span className="text-slate-200">{c.agent_name}</span> ↔ <span className="font-mono">{fmtPhone(c.client_number)}</span> · {fmtDateTime(c.created_at)}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    {a?.call_type && <Pill tone="violet">{a.call_type}</Pill>}
                    {a?.sentiment && <Pill tone={sentimentTone(a.sentiment)}>{a.sentiment}</Pill>}
                    {c.transcript_source && <Pill>{c.transcript_source} transcript</Pill>}
                    {c.recording_status === "missing" && <Pill tone="red" testId="call-recording-missing"><MicOff className="h-3 w-3" /> not recorded{c.unrecorded_alert?.status === "sent" ? " · management alerted" : ""}</Pill>}
                    {c.recording_status === "recorded" && <Pill tone="emerald" testId="call-recording-ok"><Mic className="h-3 w-3" /> recorded</Pill>}
                    {a && a.evaluable === false && <Pill tone="amber">not evaluable</Pill>}
                    {c.feedback_email?.status === "sent" && <Pill tone={c.feedback_email.cc_applied ? "amber" : "blue"}><Mail className="h-3 w-3" /> {c.feedback_email.cc_applied ? "emailed + CC managers" : "emailed to agent"}</Pill>}
                  </div>
                </div>
              </div>
              {isAdmin && (
                <div className="flex flex-wrap gap-2" data-testid="call-admin-actions">
                  <Button data-testid="call-analyze-button" size="sm" variant="outline" className="h-8 text-xs border-[#2d3d58] bg-[#0f172a] hover:bg-[#161f30]" disabled={analyze.isPending} onClick={() => analyze.mutate()}>
                    {analyze.isPending ? <Spinner /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />} {a ? "Re-analyze" : "Analyze now"}
                  </Button>
                  <TranscriptPaste callId={callId} onDone={refresh} />
                  {a && a.evaluable !== false && (
                    <Button data-testid="call-resend-feedback-button" size="sm" variant="outline" className="h-8 text-xs border-[#2d3d58] bg-[#0f172a] hover:bg-[#161f30]" disabled={resend.isPending} onClick={() => resend.mutate()}>
                      {resend.isPending ? <Spinner /> : <Mail className="h-3.5 w-3.5 mr-1.5" />} {c.feedback_email?.status === "sent" ? "Resend feedback email" : "Send feedback email"}
                    </Button>
                  )}
                </div>
              )}
            </SheetHeader>

            <Tabs defaultValue={a ? "coaching" : "transcript"} className="px-6 py-4">
              <TabsList className="bg-[#111827] border border-[#1f293d]">
                <TabsTrigger value="coaching" data-testid="call-tab-coaching">Coaching</TabsTrigger>
                <TabsTrigger value="transcript" data-testid="call-tab-transcript">Transcript</TabsTrigger>
                <TabsTrigger value="email" data-testid="call-tab-email">Email dispatch</TabsTrigger>
              </TabsList>

              <TabsContent value="coaching" className="space-y-6 pt-4">
                {!a ? (
                  <div className="panel p-5 text-sm text-slate-400" data-testid="call-no-analysis">
                    {c.analysis_status === "failed" ? <>Analysis failed: <span className="text-red-300">{c.analysis_error}</span></> : c.transcript_status === "absent" ? <>No transcript or recording was available from OpenPhone for this call. Enable <b>call recording</b> or <b>AI call transcripts</b> in OpenPhone, or paste a transcript manually.</> : c.transcript_status === "completed" ? "Transcript is ready — analysis is queued and runs within a few minutes." : <>Waiting for OpenPhone to publish a transcript. {c.transcript_note && <span className="text-slate-500">({c.transcript_note})</span>}</>}
                  </div>
                ) : a.evaluable === false ? (
                  <div className="panel p-5 text-sm text-slate-300" data-testid="call-not-evaluable"><b>Not evaluable.</b> {a.not_evaluable_reason}</div>
                ) : (
                  <>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="panel p-4 space-y-2.5" data-testid="call-score-breakdown">
                        <div className="eyebrow">Score breakdown</div>
                        {DIMS.map(([k, label]) => {
                          const v = a.scores?.[k];
                          const t = scoreTone(v);
                          return (
                            <div key={k}>
                              <div className="flex justify-between text-xs mb-1"><span className="text-slate-300">{label}</span><span className={cn("font-mono font-bold", t.text)}>{v ?? "—"}</span></div>
                              <div className="h-1.5 bg-[#161f30] rounded"><div className="h-1.5 rounded transition-[width] duration-500" style={{ width: `${v || 0}%`, background: t.hex }} /></div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="panel p-4 space-y-3">
                        <div><div className="eyebrow">Summary</div><p className="text-sm text-slate-300 leading-relaxed mt-1" data-testid="call-summary">{a.summary}</p></div>
                        <div><div className="eyebrow">Customer intent</div><p className="text-sm text-slate-300 mt-1">{a.customer_intent}</p></div>
                        <div><div className="eyebrow">Outcome</div><p className="text-sm text-slate-300 mt-1">{a.outcome}</p></div>
                      </div>
                    </div>
                    <div className="panel p-4 border-violet-500/30" data-testid="call-coaching-message">
                      <div className="eyebrow text-violet-300 mb-2 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Coaching notes for {c.agent_name?.split(" ")[0]}</div>
                      {(a.coaching_message || "").split("\n").filter(Boolean).map((p, i) => <p key={i} className="text-sm text-slate-200 leading-relaxed mb-2 last:mb-0">{p}</p>)}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <Bullets title="What went well" items={a.strengths} icon={CheckCircle2} tone="text-emerald-400" testId="call-strengths" />
                      <Bullets title="How it could have been better" items={a.improvements} icon={TrendingDown} tone="text-amber-400" testId="call-improvements" />
                      <Bullets title="Missed opportunities" items={a.missed_opportunities} icon={Target} tone="text-blue-400" testId="call-missed" />
                      <Bullets title="Try saying" items={a.suggested_phrases} icon={MessageSquareQuote} tone="text-cyan-400" testId="call-phrases" />
                      <Bullets title="Compliance flags" items={a.compliance_flags} icon={ShieldAlert} tone="text-red-400" testId="call-compliance" />
                      <Bullets title="Key moments" items={a.key_moments} icon={Lightbulb} tone="text-violet-400" testId="call-moments" />
                    </div>
                  </>
                )}
              </TabsContent>

              <TabsContent value="transcript" className="pt-4">
                {c.transcript?.length ? (
                  <div className="space-y-2" data-testid="call-transcript">
                    {c.transcript.map((s, i) => (
                      <div key={i} className={cn("flex", s.speaker === "agent" ? "justify-end" : "justify-start")}>
                        <div className={cn("max-w-[85%] rounded-lg px-3 py-2 text-sm leading-relaxed border", s.speaker === "agent" ? "bg-blue-600/15 border-blue-500/30 text-slate-100" : s.speaker === "customer" ? "bg-[#161f30] border-[#2d3d58] text-slate-200" : "bg-[#111827] border-[#1f293d] text-slate-300")}>
                          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-0.5">
                            {s.speaker === "agent" ? c.agent_name : s.speaker === "customer" ? "Customer" : "Transcript"}
                            {typeof s.start === "number" && s.start > 0 && <span className="ml-2">{Math.floor(s.start / 60)}:{String(Math.floor(s.start % 60)).padStart(2, "0")}</span>}
                          </div>
                          {s.text}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="panel p-5 text-sm text-slate-400" data-testid="call-no-transcript">{c.transcript_status === "absent" ? "No transcript available from OpenPhone for this call." : "Transcript not yet available."}</div>
                )}
              </TabsContent>

              <TabsContent value="email" className="pt-4 space-y-3">
                {c.feedback_email ? (
                  <div className="panel p-4 text-sm space-y-2" data-testid="call-email-status">
                    <div className="flex items-center gap-2"><Pill tone={c.feedback_email.status === "sent" ? "emerald" : c.feedback_email.status === "failed" ? "red" : "slate"}>{c.feedback_email.status}</Pill>{c.feedback_email.sent_at && <span className="text-xs text-slate-500 font-mono">{fmtDateTime(c.feedback_email.sent_at)}</span>}</div>
                    <div><span className="text-slate-500">To:</span> <span className="font-mono text-slate-200">{c.feedback_email.to?.join(", ") || "—"}</span></div>
                    <div><span className="text-slate-500">CC:</span> <span className="font-mono text-slate-200">{c.feedback_email.cc?.length ? c.feedback_email.cc.join(", ") : "—"}</span> {c.feedback_email.cc_applied && <span className="text-xs text-amber-300 ml-1">(score below threshold → managers copied)</span>}</div>
                    {c.feedback_email.error && <div className="text-red-300 text-xs">{c.feedback_email.error}</div>}
                  </div>
                ) : (
                  <div className="panel p-5 text-sm text-slate-400" data-testid="call-email-none">No coaching email has been sent for this call{a ? " (agent not monitored or feedback emails disabled)" : " yet"}.</div>
                )}
                {c.op_summary?.summary && <div className="panel p-4"><div className="eyebrow mb-1">OpenPhone summary</div><p className="text-sm text-slate-300">{Array.isArray(c.op_summary.summary) ? c.op_summary.summary.join(" ") : c.op_summary.summary}</p></div>}
              </TabsContent>
            </Tabs>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
